import axios from '../request'
import requestAxios from 'axios'

export interface FetchStudentCheckinNotifyParams {
  classId?: number
  [property: string]: any
}

export type StudentCheckinAttendanceStatus = 'NORMAL' | 'ABSENT' | 'ABNORMAL' | 'LEAVE'

export type StudentCheckinAttendanceTag =
  'NORMAL' |
  'EXEMPTED' |
  'ABSENT' |
  'EARLY_LEAVE' |
  'LATE' |
  'PARTIAL_ABSENT' |
  'SICK_LEAVE' |
  'PERSONAL_LEAVE'

export type StudentCheckinCheckInType = 'ORDINARY' | 'QR_CODE' | 'CODE' | 'PHOTO'

export type StudentCheckinCheckInRecordItem = {
  checkInTime?: string
  deviceInfo?: string
  isChecked?: boolean
  locationAddr?: string
  photo?: string
  roundId?: number
  roundNum?: number
  [property: string]: any
}

export type StudentCheckinDeviceDupItem = {
  deviceInfo: string
  studentId: number
  studentName: string
  [property: string]: any
}

export type StudentCheckinAttendanceRecord = {
  attendanceId: number
  attendanceStatus?: StudentCheckinAttendanceStatus
  attendanceTag?: StudentCheckinAttendanceTag
  checkInRecordList?: StudentCheckinCheckInRecordItem[]
  checkInType?: StudentCheckinCheckInType
  courseWeek?: number
  dayOfWeek?: number
  existMultipleTasksByDay?: boolean
  exemptAllCourseSigns?: boolean
  isOverTwoDevice?: boolean
  lateJoinFlag?: boolean
  nextAttendanceId: number | null
  preAttendanceId: number | null
  preparationId?: null
  preparationName?: null
  remarks?: string
  studentId?: number
  taskId?: number
  taskIndex?: number
  taskName?: string
  taskPublishTime?: string
  teacher?: string
  teacherPhoto?: string
  courseId?: number
  courseManagerName?: string
  courseManagerPhoto?: string
  deviceDupFlag?: boolean
  deviceDupList?: StudentCheckinDeviceDupItem[]
  isTeacherHandled?: boolean
  [property: string]: any
}

export type StudentCheckinDetailRequest = {
  classId: number
  attendanceId: number
  filterAbnormalPrevNext?: boolean
}

export type StudentCheckinDetailResponse = {
  code: string
  success: boolean
  message: string
  type: string
  content: StudentCheckinAttendanceRecord
  [property: string]: any
}

export type StudentCheckinNotifyTask = {
  attendanceId?: number
  attendanceStatus?: StudentCheckinAttendanceStatus
  classId?: number
  courseId?: number
  courseName?: string
  courseWeek?: number
  dayOfWeek?: number
  roundNum?: number
  taskId?: number
  taskIndex?: number
  taskName?: string
  taskPublishTime?: string
  existMultipleTasksByDay?: boolean
  hasPermission?: boolean
  /** 位置限制开关，缺省 true（存量任务行为不变） */
  locationRequired?: boolean
  latitude?: number
  longitude?: number
  /** 签到点名称，空则前端兜底「签到点」 */
  locationAddr?: string
  photoReq?: string
  range?: number
  checkInType?: StudentCheckinCheckInType
  /** 二维码刷新间隔（秒），仅 QR_CODE 有效；历史任务缺省时后端建议返回 5 */
  qrRefreshSeconds?: number
  roundEndTime?: string
  [property: string]: any
}

export interface CheckInNotifyContent {
  activeTaskList: StudentCheckinNotifyTask[]
  abnormalTaskList: StudentCheckinNotifyTask[]
}

export type StudentCheckinRecordCalendarRequest = {
  classId: number
  month: string
}

export type StudentCheckinRecordCalendarResponse = {
  code: string
  success: boolean
  message: string
  type: string
  content: StudentCheckinNotifyTask[]
  [property: string]: any
}

export type StudentCheckInForm = {
  attendanceId?: number
  classId?: number
  checkInCode?: string
  latitude?: number
  longitude?: number
  photo?: string
  qrCode?: string
  [property: string]: any
}

export type PendingSignInTask = {
  taskId?: number
  attendanceId?: number
  courseId?: number
  classId?: number
  courseName?: string
  taskIndex?: number
  checkInType?: StudentCheckinCheckInType
  taskPublishTime?: string
  attendanceStatus?: StudentCheckinAttendanceStatus
  /** 二维码刷新间隔（秒），仅 QR_CODE 有效 */
  qrRefreshSeconds?: number
  [property: string]: any
}

export type CheckRoundInfoItem = {
  roundNum: number
  isChecked: boolean
  checkInTime: string
  [property: string]: any
}

export async function fetchStudentCheckinNotify(params: FetchStudentCheckinNotifyParams): Promise<FetchResponse<CheckInNotifyContent>> {
  return axios.get('/task/student/check-in-notify', { params })
}

export async function fetchStudentCheckinRecordCalendar(params: StudentCheckinRecordCalendarRequest): Promise<StudentCheckinRecordCalendarResponse> {
  return axios.get('/task/student/check-in-record', { params })
}

export async function fetchStudentCheckinDetail(params: StudentCheckinDetailRequest): Promise<StudentCheckinDetailResponse> {
  return axios.get('/task/student/check-in-detail', { params })
}

export async function fetchSubmitCheckin(data: StudentCheckInForm): Promise<FetchResponse<any>> {
  return axios.post('/task/student/check-in', data)
}

export interface AmapRegeoResponse {
  status: string
  info?: string
  infocode?: string
  regeocode?: {
    formatted_address?: string
    [property: string]: any
  }
  [property: string]: any
}

export async function fetchAmapRegeo(longitude: string | number, latitude: string | number): Promise<AmapRegeoResponse> {
  return axios.request({
    url: 'https://amap.aiuclass.com/v3/geocode/regeo',
    params: {
      location: `${longitude},${latitude}`,
    }
  })
}

export async function fetchHwRegeo(longitude: string | number, latitude: string | number): Promise<AmapRegeoResponse> {
  return axios.request({
    url: 'https://hwmap.aiuclass.com/mapApi/v1/siteService/reverseGeocode',
    method: 'POST',
    data: {
      location: {
        'lng': longitude,
        'lat': latitude
      }
    }
  })
}


export async function fetchCheckRoundInfo(classId: string | number): Promise<FetchResponse<CheckRoundInfoItem[]>> {
  return axios.get('/task/student/check-in-round-info', {
    params: {
      classId
    }
  })
}

type CheckInRoundTask = {
  taskId?: number
  taskType?: string
  taskPublishTime?: string | null
  classId?: number | string | null
  interactiveTotalRounds?: number | null
  interactiveHitRounds?: number | null
}

type CheckInRoundGroup = {
  attendanceRecordList?: Array<{
    taskId?: number
    checkInRecordList?: Array<{ isChecked?: boolean }>
  }>
}

export async function attachCheckInRounds<T extends CheckInRoundTask>(tasks: T[], classId?: string | number): Promise<T[]> {
  const pending = tasks.filter(task => task.taskType === 'CHECK_IN' && !(Number(task.interactiveTotalRounds) > 0))
  if (!pending.length) return tasks
  const resolvedClassId = classId ?? pending.find(task => task.classId)?.classId
  if (!resolvedClassId) return tasks
  const months = new Set<string>()
  pending.forEach(task => {
    const month = String(task.taskPublishTime || '').slice(0, 7)
    if (/^\d{4}-\d{2}$/.test(month)) months.add(month)
  })
  if (!months.size) {
    const now = new Date()
    months.add(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`)
  }
  const byTask = new Map<number, { total: number, hit: number }>()
  await Promise.all([...months].map(async month => {
    try {
      const { success, content } = await fetchStudentCheckinRecordCalendar({
        classId: Number(resolvedClassId),
        month,
      })
      const groups = Array.isArray(content) ? content as CheckInRoundGroup[] : []
      if (!success) return
      groups.forEach(group => {
        ;(group.attendanceRecordList || []).forEach(record => {
          const rounds = record.checkInRecordList || []
          if (!record.taskId || !rounds.length) return
          const hit = rounds.filter(round => round.isChecked).length
          const prev = byTask.get(record.taskId)
          if (!prev || rounds.length > prev.total) {
            byTask.set(record.taskId, { total: rounds.length, hit })
          }
        })
      })
    } catch (error) {
      console.error(error)
    }
  }))
  if (!byTask.size) return tasks
  return tasks.map(task => {
    if (task.taskType !== 'CHECK_IN' || task.taskId == null) return task
    const found = byTask.get(task.taskId)
    if (!found) return task
    return {
      ...task,
      interactiveTotalRounds: found.total,
      interactiveHitRounds: found.hit,
    }
  })
}

export async function getPendingSignInTasks() {
  const { success, content, message, code } = await fetchStudentCheckinNotify({})
  return {
    code: code || '',
    msg: message,
    data: success ? (content?.activeTaskList || []) as PendingSignInTask[] : []
  }
}

export interface FetchAbsentCountParams {
  classId: number
}

export async function fetchAbsentCount(params: FetchAbsentCountParams): Promise<FetchResponse<number>> {
  return axios.get('/task/student/absent-count', { params })
}
